import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';

import { get } from 'helpers/api';
import SignedCA from './signed_ca';
import VaultActivityTable from './vault_activity_table';
import { refreshLead, updateLeadFields, updateLeadLocally } from '../../../../actions/leads';
import ShareLinkByEmailModal from 'components/documents/share_link_by_email_modal';
import { getProject } from '../../../../selectors';
import { Alert } from 'components/ui/alert';
import LoadingSpinner from 'components/ui/loading_spinner';

export function VaultActivity({
  compact,
  propertyVaultProfile,
  lead,
  refreshLead,
  project,
  useVaultGates
}) {
  const [activity, setActivity] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showCaInviteModal, setShowCaInviteModal] = useState(false);
  const openCaInviteModal = () => setShowCaInviteModal(true);
  const closeCaInviteModal = () => {
    setShowCaInviteModal(false);
    refreshLead(lead);
  };
  const SHARE_DOC_MODAL_DESCRIPTION =
    `Now that you have uploaded a CA for ${lead.leadData.name}, send them an invite to view your locked documents.`;
  const WITH_GATES = ` Once sent, the invitee will adhere to the remaining CA & Access settings you have configured
    for your documents.`;
  const WITHOUT_GATES = ' The invitee will skip the waitlist.';

  const {
    activityPath,
    saleOrLease
  } = propertyVaultProfile;
  React.useEffect(() => {
    if (!activityPath) return;

    (async () => {
      try {
        setActivity(await get(activityPath));
        setLoadFailed(false);
      } catch {
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [lead.emailAddress, lead.propertyVaultProfiles]);

  const updateActivity = (data) => {
    setActivity({ ...activity, caDownloadPath: data.caUrl, caName: data.caLinkLabel, isValidCa: true });
    refreshLead(lead); // so CA banner can update
    if (lead.leadData.email) openCaInviteModal();
  };

  const isFullyFinished = !!lead.profileUserType;
  const signedCa = activity.caSignedAt !== null;


  const caSection = () => {
    if (compact) {
      return (
        <div>
          <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
            <div className="fw-semibold fs-large">Confidentiality Agreement</div>
            {!loading && !loadFailed && (
              <Badge appearance="muted" variant={signedCa ? 'primary' : 'secondary'}>
                {signedCa ? <><FontAwesomeIcon icon={faCircleCheck} />Signed</> : 'Not signed'}
              </Badge>
            )}
          </div>
          {caContent()}
        </div>
      );
    }

    return (
      <div>
        <div className="form-label mb-1">
          Confidentiality Agreement
        </div>
        {caContent()}
      </div>
    );
  };

  const caContent = () => {
    if (loadFailed) {
      return (
        <Alert name="vault-activity-error" severity="destructive">
          The CA and document activity couldn&apos;t be loaded. Refresh the page to try again.
        </Alert>
      );
    }

    if (loading) {
      return (
        <div className="d-flex justify-content-center mt-4">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (activity.caUploadPath) {
      return (
        <SignedCA
          caDownloadPath={activity.caDownloadPath}
          caName={activity.caName}
          caUploadPath={activity.caUploadPath}
          compact={compact}
          isDisabled={grayedOut}
          isValidCa={activity.isValidCa}
          lead={lead}
          refreshLead={refreshLead}
          saleOrLease={saleOrLease}
          signedCa={signedCa}
          onSuccess={updateActivity}
        />
      );
    }

    // Unreachable: both branches of VaultsController#property_vault_profile_activity always
    // return a ca_upload_path, and until the request resolves `loading` shows the spinner above.
    return null;
  };

  const grayedOut = () => {
    if (saleOrLease === 'Sale') {
      return !project.saleChecked;
    } else if (saleOrLease === 'Lease') {
      return !project.leaseChecked;
    }
  };

  return (
    <div style={{ opacity: grayedOut() ? 0.5 : 1 }}>
      <hr></hr>
      <h6 className="mb-4">{saleOrLease} Status</h6>
      <div>
        {caSection()}
        {!isEmpty(activity.docActivity) && isFullyFinished && (
          <div className="mt-5">
            <div className="form-label mb-1">Site Activity</div>
            <VaultActivityTable activities={activity.docActivity} />
          </div>
        )}
        <ShareLinkByEmailModal
          additionalInviteProps={{ sale_or_lease: propertyVaultProfile.saleOrLease.toLowerCase() }}
          description={`${SHARE_DOC_MODAL_DESCRIPTION}${useVaultGates ? WITH_GATES : WITHOUT_GATES}`}
          email={lead.leadData.email}
          fromEmails={project.brokersForShareDocs}
          hideModal={closeCaInviteModal}
          leadId={lead.id}
          propertyId={project.id}
          show={showCaInviteModal}
          title="Send email to view documents"
        />
      </div>
    </div>
  );
}

VaultActivity.propTypes = {
  compact: PropTypes.bool,
  lead: PropTypes.shape({
    id: PropTypes.number.isRequired,
    profileUserType: PropTypes.string
  }).isRequired,
  project: PropTypes.object.isRequired,
  propertyVaultProfile: PropTypes.shape({
    activityPath: PropTypes.string,
    approvalState: PropTypes.string,
    approveAccessPath: PropTypes.string,
    id: PropTypes.number,
    inWaitlist: PropTypes.bool,
    revokeAccessPath: PropTypes.string,
    saleOrLease: PropTypes.string.isRequired
  }).isRequired,
  refreshLead: PropTypes.func.isRequired,
  updateLeadFields: PropTypes.func.isRequired,
  updateLeadLocally: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired
};

const actions = {
  refreshLead,
  updateLeadLocally,
  updateLeadFields
};

const mapStateToProps = (state) => {
  return {
    project: getProject(state),
    useVaultGates: state.project.useVaultGates
  };
};

export default connect(mapStateToProps, actions)(VaultActivity);
